import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  useClock,
  useInterestSave,
  useInterestSaves,
  useInterestTopics,
  useInterestsOverview,
} from '../lib/data'
import { DataModeBadge } from '../components/HeaderBand'
import { AskBar } from '../components/interests/AskBar'
import { ClusterRow } from '../components/interests/ClusterRow'
import { DetailPane } from '../components/interests/SaveDetail'
import { SaveCard } from '../components/interests/SaveCard'
import { ArrowRight } from '../components/interests/icons'
import { FilterPill, QuietNote, Skeleton, Unavailable } from '../components/interests/parts'
import { itemsLine, libraryLine, moreInCluster, oldestLine } from '../components/interests/labels'

// ============================================================================
// INTERESTS — docs/design/handoff/"LifeOS Interests.dc.html", option 7a.
//
// PURPOSE (README §6): make 247 saved posts useful instead of an archive.
//
// THE ORDER OF THE PAGE IS THE WHOLE ARGUMENT, and it is the one thing about
// this screen that must never be rearranged for convenience:
//
//   1. THE ASK BAR, first and loudest, inside the header band. A corpus you can
//      ask a question of is a library; the same 247 posts in a grid are a
//      shoebox. It is the page's main event even though the engine behind it
//      arrives with V4 — so it renders full size and submits to a notice that
//      names V4, rather than shrinking into a disabled input.
//   2. THE CLUSTERS, second, auto-grouped and admitting it: `clusters regrouped
//      nightly`. A machine's current opinion, not the owner's filing.
//   3. THE LIBRARY, third — a grid of cards, ONE CARD PER SAVE, each of them the
//      clickable thing. When a cluster is active the grid is that cluster, with
//      the count stated (`14 saves · 10 shown · newest first`), the rest reachable
//      through `4 more in AI video models`, and the age of the oldest save in
//      the cluster in the quietest type on the screen.
//
//      THE LIBRARY IS NOT DOWNSTREAM OF THE CLUSTERING, and wiring it that way
//      was the 2026-08-28 bug: with no nightly job in production `getInterest
//      Topics` answers `[]` (remote.ts says so outright), and the grid was gated
//      on an active cluster — so three real saves rendered as a sentence about
//      grouping plus one right-hand pane, and the owner's saves had no cards at
//      all. Order three of the argument is "library third", not "library only if
//      the clustering ran". An ungrouped library is still a library; it is the
//      SHELF it goes on that the clustering supplies, and that absence is stated
//      beside the empty cluster row where it is true.
//
// SELECTION LIVES IN THE URL (`?cluster=`, `?save=`). README §State Management
// lists `interests.activeCluster` and `selectedSaveId` as client state; putting
// them in the query string makes one save shareable and one screen state
// photographable, which is what the port-proof shots need.
//
// THE READS ARE LIVE (overview, topics, saves, save detail — remote.ts). What still throws is
// the FILTERING LENS behind most of the pills above the grid (Type/Source/Date, V5) and the ask
// bar's engine (V4) — each region names the version it is actually waiting on rather than
// rendering an empty one, because an empty cluster row would claim "nothing has been grouped"
// and an empty grid would claim "you have saved nothing", and neither is what a stalled FILTER
// means.
// ============================================================================

/** The mockup shows ten of fourteen. Above this, the cluster keeps the rest behind one row. */
const SHOWN_BY_DEFAULT = 10

export function Interests() {
  const [params, setParams] = useSearchParams()
  const [expanded, setExpanded] = useState(false)
  // THE ONE LIVE FILTER. `SaveFilter.unread` is real in both `remote.ts` and `demo.ts`
  // (components/interests/parts.tsx → `FilterPill`'s header comment); Type/Source/Date still
  // wait on V5 and stay disabled-by-design below.
  const [unreadOnly, setUnreadOnly] = useState(false)

  const { data: clock } = useClock()
  const overview = useInterestsOverview()
  const topics = useInterestTopics()

  // The clusters come back most-recently-added-to first, so the first one is the live interest.
  const activeId = params.get('cluster') ?? topics.data?.[0]?.id
  const activeTopic = topics.data?.find((t) => t.id === activeId)

  const saves = useInterestSaves({
    ...(activeTopic ? { topic: activeTopic.label } : {}),
    ...(unreadOnly ? { unread: true } : {}),
  })
  const rows = saves.data ?? []
  const shownRows = expanded ? rows : rows.slice(0, SHOWN_BY_DEFAULT)
  const remaining = rows.length - shownRows.length

  const selectedId = params.get('save') ?? shownRows[0]?.id
  const detail = useInterestSave(selectedId)

  // The nightly job answered, and answered with nothing — which is a different statement from
  // "the clusters have not loaded", and only the first one is worth a sentence on the screen.
  const ungrouped = !activeTopic && topics.data?.length === 0

  // Rows arrive newest-first, so the last one is the oldest thing in an ungrouped library.
  const oldest = activeTopic?.oldestSavedAt ?? rows[rows.length - 1]?.savedAt

  function select(next: Record<string, string | null>) {
    const merged = new URLSearchParams(params)
    for (const [key, value] of Object.entries(next)) {
      if (value === null) merged.delete(key)
      else merged.set(key, value)
    }
    setParams(merged, { replace: true })
  }

  return (
    <>
      {/* The band is a COLUMN here, not the shared row: the ask bar lives inside it, which is
          what puts the page's main event above the fold and above the clusters. */}
      <div
        className="flex flex-none flex-col gap-[14px] px-[30px] pb-[14px] pt-[18px]"
        style={{ background: 'var(--band-interests)' }}
      >
        <div className="flex items-center gap-[14px]">
          <h1 className="flex-none text-screen font-semibold">Interests</h1>

          {overview.data && (
            <div className="flex items-center gap-[9px] text-[11.5px] font-normal leading-none text-muted">
              <span>{itemsLine(overview.data.totalSaves)}</span>
              <Dot />
              <span>{overview.data.inboxCount} in inbox</span>
              {overview.data.backlogImport && (
                <>
                  <Dot />
                  <span>
                    {overview.data.backlogImport.source} import:{' '}
                    {overview.data.backlogImport.items} items
                  </span>
                  {overview.data.backlogImport.done && (
                    <span className="flex items-center gap-[5px] font-medium text-success">
                      <span className="size-[5px] rounded-full bg-live-dot" aria-hidden />
                      done
                    </span>
                  )}
                </>
              )}
            </div>
          )}
          {/* No placeholder counts while it loads and none when the read failed: a made-up
              "0 items" is the one claim this band must never make. */}
          {overview.error && (
            <span
              className="text-[11.5px] leading-none text-microcopy"
              title={overview.error instanceof Error ? overview.error.message : undefined}
            >
              counts arrive with V5
            </span>
          )}

          <div className="ml-auto flex flex-none items-center gap-[7px]">
            <FilterPill
              label="Inbox"
              count={overview.data?.inboxCount}
              dark
              waitingFor="The inbox lens arrives with V5, the capture endpoint. The count beside it is real."
            />
            {['Type', 'Source'].map((label) => (
              <FilterPill
                key={label}
                label={label}
                waitingFor={`Filtering by ${label.toLowerCase()} arrives with V5, the Interests backend.`}
              />
            ))}
            <FilterPill
              label="Unread"
              pressed={unreadOnly}
              onClick={() => setUnreadOnly((v) => !v)}
            />
            <FilterPill
              label="Date"
              waitingFor="Filtering by date arrives with V5, the Interests backend."
            />
            <DataModeBadge />
          </div>
        </div>

        <AskBar />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-[13px] overflow-hidden px-[30px] pb-[26px]">
        <ClusterRow
          topics={topics.data}
          activeId={activeId}
          onSelect={(id) => {
            setExpanded(false)
            select({ cluster: id, save: null })
          }}
          isPending={topics.isPending}
          error={topics.error}
        />

        <div className="flex min-h-0 flex-1 gap-[13px]">
          {/* Named, which the mockup does not draw and cannot: an accessible name is the
              codebase's job, like the focus ring (FIDELITY rule 3). It makes the library a
              landmark a screen reader can jump to, and it is what separates these cards from
              the cluster chips above them, which are also pressable. */}
          <section aria-label="Saves" className="flex min-w-0 flex-1 flex-col gap-[11px]">
            {/* The clustering's absence is a fact about the CLUSTERS, so it is stated here,
                directly under the row that has no chips in it — and never in place of the
                library, which is a different collection that is not missing. */}
            {ungrouped && (
              <QuietNote>
                The nightly clustering has not grouped anything yet, so there is no cluster to
                open. Everything saved is still here — it is the grouping that is missing, not
                the library.
              </QuietNote>
            )}

            {saves.error ? (
              <Unavailable error={saves.error} />
            ) : saves.isPending || topics.isPending ? (
              <div className="grid grid-cols-2 gap-3" aria-hidden>
                {Array.from({ length: 6 }, (_, i) => (
                  <Skeleton key={i} height={126} />
                ))}
                <span className="sr-only">Loading…</span>
              </div>
            ) : rows.length === 0 ? (
              <QuietNote>
                {activeTopic
                  ? 'This cluster came back empty, which means the nightly run moved everything out of it. It will be gone at the next regroup.'
                  : 'Nothing has been saved yet, so there is no library to show. The first capture will appear here.'}
              </QuietNote>
            ) : (
              <>
                <div className="flex flex-none items-baseline gap-[9px]">
                  {/* The heading names the shelf. With no clustering there is no shelf name to
                      borrow, and inventing one would be the screen filing saves the nightly job
                      never filed — so it says what the grid actually is. */}
                  <h2 className="text-[12.5px] font-semibold leading-none">
                    {activeTopic ? activeTopic.label : 'All saves'}
                  </h2>
                  <span className="text-[11px] leading-none text-microcopy">
                    {libraryLine(rows.length, shownRows.length)}
                  </span>
                </div>

                <div className="-mr-2 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-2">
                  <div className="grid grid-cols-2 content-start gap-3">
                    {shownRows.map((save) => (
                      <SaveCard
                        key={save.id}
                        save={save}
                        today={clock?.date ?? save.savedAt.slice(0, 10)}
                        selected={save.id === selectedId}
                        onSelect={() => select({ save: save.id })}
                      />
                    ))}
                  </div>

                  {/* The expander carries the library's age, which is the quiet fact that tells
                      the owner whether this is a live interest or a fossil. Clustered, the
                      cluster is authoritative about its own oldest save — it counts the ones
                      past the fetched page too. Ungrouped, the rows ARE the library, newest
                      first, so the last one is the oldest. */}
                  <div className="flex flex-none items-center gap-[10px] rounded-[12px] border border-dashed border-dash px-[15px] py-[11px]">
                    {remaining > 0 ? (
                      <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="flex items-center gap-[10px] text-[12px] font-medium leading-none text-ink-3 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-ink"
                      >
                        {moreInCluster(remaining, activeTopic?.label)}
                        <ArrowRight size={13} strokeWidth={2} className="text-muted" />
                      </button>
                    ) : (
                      <span className="text-[12px] font-medium leading-none text-ink-3">
                        All {rows.length} shown
                      </span>
                    )}
                    {oldest && (
                      <span className="ml-auto text-[10.5px] leading-none text-faintest-2">
                        {oldestLine(oldest, Boolean(activeTopic))}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>

          <DetailPane
            detail={detail.data}
            today={clock?.date ?? ''}
            isPending={Boolean(selectedId) && detail.isPending}
            error={detail.error}
            onSelect={(id) => select({ save: id })}
          />
        </div>
      </div>
    </>
  )
}

function Dot() {
  return <span className="size-[3px] flex-none rounded-full bg-dash" aria-hidden />
}
